import { IonList, IonMenu, IonText } from '@ionic/react';
import { useSubscription } from '@meraki-internal/state';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { PageContent } from '../components/page/PageContent.tsx';
import { MapIcon, PlaneIcon, ChatIcon, InsuranceIcon, ListIcon, LightbulbIcon, SettingsIcon, PriceTagIcon, GiftIcon, DroneIcon } from '../components/icons/Icons.tsx';
import { useEffect, useRef, useState } from 'react';
import { mediaNotLargerScreen } from '../support/helpers/breakpoints.ts';
import { DeviceStatusBar } from '../support/DeviceStatusBar.ts';
import { AlertPresenter } from '../app/AlertBinder.tsx';
import { IntercomService } from '../app/intercom/IntercomService.ts';
import { MissionsState } from '../missions/model/MissionsState.ts';
import { createUseStyles } from 'react-jss';
import { IfNative } from '../support/IfNative.tsx';
import { MenuViewModel } from './MenuViewModel.ts';
import { MenuItem } from './MenuItem.tsx';
import { MenuHeader } from './MenuHeader.tsx';
import { PaywallMenuButton } from '../paywall/PaywallMenuButton.tsx';
import { AffiliateLinkState } from '../affiliates/AffiliateLinkState.ts';

export const MENU_COLLAPSED_WIDTH = 66; // includes shadow

type IStyleKeys = 'menu' | 'menuCollapsed' | 'menuShadow';
interface IStyleProps { animating: boolean }

const useStyles = createUseStyles<IStyleKeys, IStyleProps>({
    menu: {
        zIndex: ({animating}) => animating ? 9999 : 10001
    },
    menuCollapsed: {
        [mediaNotLargerScreen]: {
            display: 'none'
        },
        zIndex: 10000,
        display: 'flex',
        width: MENU_COLLAPSED_WIDTH - 2,
        '&::part(container)': {
            transform: 'unset',
            WebkitTransform: 'unset',
        },
        '&::part(backdrop)': {
            display: 'none'
        }
    },
    menuShadow: {
        [mediaNotLargerScreen]: {
            display: 'none'
        },
        zIndex: 9998,
        position: 'absolute',
        top: 0,
        left: 0,
        width: MENU_COLLAPSED_WIDTH - 2,
        height: '100%',
        boxShadow: '0px 0px 2px 1px var(--ion-color-step-200)'
    }
});

const MenuContent: React.FC = () => {
    const affiliatesState = useInstance(AffiliateLinkState);
    useSubscription(() => affiliatesState);

    const statusBar = useInstance(DeviceStatusBar);

    const menuVM = useInstance(MenuViewModel);

    const alert = useInstance(AlertPresenter);

    const intercom = useInstance(IntercomService);
    useSubscription(() => intercom);

    const missions = useInstance(MissionsState);
    useSubscription(() => missions);

    const onClickChat = async () => {
        if (intercom.isEnabled()) {
            await intercom.displayMessenger();
        } else {
            alert.showAlert({
                header: 'Sorry! Something went wrong',
                message: 'We were unable to load the chat messenger, please try again later.'
            });
        }
    };

    return (
        <PageContent noScroll onClick={() => menuVM.toggle()}>

            <MenuHeader />

            <IonList>
                <MenuItem icon={MapIcon} bold title="Map" url="/map" />
                <MenuItem icon={PlaneIcon} bold title="Missions" url="/missions" reminderCount={missions.getReminderCount()} />

                <MenuItem icon={ListIcon} bold title="Checklists" url="/checklists" />

                <MenuItem icon={DroneIcon} bold title="Equipment" url="/fleet" />

                <MenuItem icon={InsuranceIcon} bold title="Drone Insurance" url="/insurance"  newFeatureKey="insurance-v2" newFeatureLabel="It's Back" />

                <MenuDivider />

                <MenuItem small title="Tips and Help" icon={LightbulbIcon} onClick={() => intercom.showHelpCenter()}/>

                <IfNative>
                    <MenuItem small title="Chat" icon={ChatIcon} onClick={onClickChat} reminderCount={intercom.state.unreadCount} />
                </IfNative>

                <MenuItem small title="Settings" icon={SettingsIcon} url="/settings" otherUrls={['/invite', '/calendar']} />

                <MenuDivider />

                <PaywallMenuButton />

                {affiliatesState.getAffiliateLinks().filter(a => a.showInMenu).map((a, i) => (
                    <MenuItem small key={`affiliate-${i}`} title={a.offer} icon={PriceTagIcon} onClick={() => affiliatesState.affiliateLinkClicked(a, {source: 'menu'})} color="success" />
                ))}

                {affiliatesState.anyNotInMenu() && (
                    <MenuItem small title="More Pilot Perks" icon={GiftIcon} url="/marketplace" color="tertiary" />
                )}

            </IonList>

            <div style={{position: 'absolute', bottom: 30 + statusBar.getNavigationBarHeight(), width: '100%', maxHeight: '10vh', textAlign: 'center'}}>
                <IonText style={{display: 'block', fontSize: 14}} color="medium">FAA-Approved</IonText>
                <img src="./assets/images/faa-badge-group.png" alt="FAA Approved" width={120} />
            </div>
        </PageContent>
    );
};

const MenuDivider = () => {
    return (
        <hr style={{margin: '5px 0 5px 64px', backgroundColor: '#d0d0d0'}} />
    );
};

export const Menu: React.FC<{ className?: string, outlet: string }> = ({ className, outlet }) => {
    const [animating, setAnimating] = useState(false);
    const classes = useStyles({ animating });

    const menuRef = useRef<HTMLIonMenuElement>(null);
    const menuVM = useInstance(MenuViewModel);

    useEffect(() => {
        if (menuRef && menuRef.current){
            menuVM.init(menuRef.current);
        }
    }, [menuRef, menuVM]);

    const willOpen = () => {
        setAnimating(true);
        menuVM.open();
    };

    const didOpen = () => {
        setAnimating(false);
    };

    const willClose = () => {
        setAnimating(true);
        menuVM.close();
    };

    const didClose = () => {
        setAnimating(false);
    };

    return (
        <div className={className}>
            <IonMenu className={classes.menuCollapsed} contentId={outlet} type="overlay" swipeGesture={false}>
                <MenuContent />
            </IonMenu>
            <div className={classes.menuShadow} />
            <IonMenu className={classes.menu} contentId={outlet} type="overlay" ref={menuRef} swipeGesture={false} onIonWillOpen={willOpen} onIonDidOpen={didOpen} onIonWillClose={willClose} onIonDidClose={didClose}>
                <MenuContent />
            </IonMenu>
        </div>
    );
};
